import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import {
  getAdventures,
  createAdventureSession,
  getAdventureScenario,
  identifyEmotion,
  chooseAction,
  completeAdventureSession,
  getProfile,
} from '../../api';
import './AventureAmitie.css';

const EMOTION_EMOJI = {
  'Joie': '😊',
  'Tristesse': '😢',
  'Colère': '😠',
  'Colere': '😠',
  'Peur': '😨',
  'Surprise': '😲',
  'Dégoût': '🤢',
  'Degout': '🤢',
  'Timidité': '😳',
  'Timidite': '😳',
  'Jalousie': '😒',
  'Frustration': '😤',
  'Sérénité': '😌',
  'Serenite': '😌',
  'Fierté': '😤',
  'Honte': '😳',
  'Calme': '😌',
};

function getEmotionEmoji(name) {
  return EMOTION_EMOJI[name] || '🙂';
}

function AventureAmitie() {
  const { token, saveAuth } = useAuth();
  const navigate = useNavigate();

  // Phase principale de l'UI
  const [phase, setPhase] = useState('adventures'); // 'adventures' | 'playing' | 'results'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [module1Required, setModule1Required] = useState(false);

  // Liste des aventures
  const [adventures, setAdventures] = useState([]);

  // Session en cours
  const [sessionId, setSessionId] = useState(null);
  const [currentAdventure, setCurrentAdventure] = useState(null);

  // Scénario en cours
  const [scenario, setScenario] = useState(null);
  const [gamePhase, setGamePhase] = useState('emotion'); // 'emotion' | 'action'
  const [question, setQuestion] = useState('');
  const [emotionChoices, setEmotionChoices] = useState([]);
  const [actionChoices, setActionChoices] = useState([]);
  const [progress, setProgress] = useState({ current: 1, total: 1 });
  const [submitting, setSubmitting] = useState(false);

  // Feedback modal
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState({});
  const [feedbackPhase, setFeedbackPhase] = useState(''); // 'emotion' | 'action'

  // Résultats finaux
  const [results, setResults] = useState(null);

  // --- Chargement des aventures ---
  const loadAdventures = useCallback(async () => {
    setLoading(true);
    setError('');
    setModule1Required(false);
    try {
      const response = await getAdventures(token);
      setAdventures(response.data);
    } catch (err) {
      if (err.status === 403 && err.data?.module1_required) {
        setModule1Required(true);
      } else {
        setError(err.message || 'Erreur lors du chargement des aventures');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (phase === 'adventures') {
      loadAdventures();
    }
  }, [phase, loadAdventures]);

  // --- Chargement du scénario ---
  const loadScenario = useCallback(async (sid) => {
    const response = await getAdventureScenario(token, sid);
    const data = response.data;
    setScenario(data.scenario);
    setGamePhase(data.phase);
    setQuestion(data.question);
    setProgress(data.progress);
    if (data.phase === 'emotion') {
      setEmotionChoices(data.emotion_choices);
    } else {
      setActionChoices(data.action_choices);
    }
  }, [token]);

  // --- Démarrer une aventure ---
  const handleSelectAdventure = async (adventure) => {
    if (!adventure.is_unlocked) return;
    setLoading(true);
    setError('');
    try {
      const response = await createAdventureSession(token, adventure.id);
      const sid = response.data.session_id;
      setSessionId(sid);
      setCurrentAdventure(adventure);
      await loadScenario(sid);
      setPhase('playing');
    } catch (err) {
      if (err.status === 422 && err.data?.session_id) {
        // Session existante — on la reprend
        const sid = err.data.session_id;
        setSessionId(sid);
        setCurrentAdventure(adventure);
        try {
          await loadScenario(sid);
          setPhase('playing');
        } catch (loadErr) {
          setError(loadErr.message || 'Erreur lors de la reprise de la session');
        }
      } else {
        setError(err.message || "Erreur lors du démarrage de l'aventure");
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Identifier l'émotion ---
  const handleIdentifyEmotion = async (emotionId) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await identifyEmotion(token, sessionId, emotionId);
      setFeedbackData(response.data);
      setFeedbackPhase('emotion');
      setShowFeedback(true);
    } catch (err) {
      setError(err.message || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Choisir une action ---
  const handleChooseAction = async (choiceId) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await chooseAction(token, sessionId, choiceId);
      setFeedbackData(response.data);
      setFeedbackPhase('action');
      setShowFeedback(true);
    } catch (err) {
      setError(err.message || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Continuer après le feedback ---
  const handleContinue = async () => {
    setShowFeedback(false);

    if (feedbackPhase === 'action') {
      if (feedbackData.adventure_completed) {
        // Finaliser la session
        try {
          const response = await completeAdventureSession(token, sessionId);
          setResults(response.data);
          // Rafraîchir le profil pour mettre à jour l'XP
          try {
            const profileResponse = await getProfile(token);
            saveAuth(profileResponse.data, token);
          } catch { /* Erreur non bloquante */ }
          setPhase('results');
        } catch (err) {
          setError(err.message || 'Erreur lors de la finalisation');
        }
      } else if (feedbackData.scenario_completed) {
        // Charger le scénario suivant
        try {
          await loadScenario(sessionId);
        } catch (err) {
          setError(err.message || 'Erreur lors du chargement du scénario suivant');
        }
      }
      // Si scenario_completed: false → le joueur réessaie la phase action
    } else {
      // Feedback de la phase émotion
      if (feedbackData.correct) {
        // Recharger le scénario qui retourne maintenant la phase action
        try {
          await loadScenario(sessionId);
        } catch (err) {
          setError(err.message || 'Erreur lors du chargement de la phase action');
        }
      }
      // Si incorrect → on ferme juste le modal et le joueur réessaie
    }
  };

  // --- Retour à la liste ---
  const handleBackToAdventures = () => {
    setPhase('adventures');
    setSessionId(null);
    setCurrentAdventure(null);
    setScenario(null);
    setResults(null);
    setShowFeedback(false);
    setFeedbackData({});
    setFeedbackPhase('');
    setError('');
  };

  // --- Rendu ---
  return (
    <div className="aventure-page">
      <header className="aventure-header">
        <div className="aventure-header-top">
          <h1>L'Aventure de l'Amitié</h1>
          {phase === 'playing' && (
            <div className="aventure-header-right">
              {currentAdventure && (
                <span className="aventure-current-badge">{currentAdventure.title}</span>
              )}
              <button className="aventure-back-btn" onClick={handleBackToAdventures}>
                ← Aventures
              </button>
            </div>
          )}
        </div>
        {phase === 'playing' && (
          <div className="aventure-progress-wrap">
            <div className="aventure-progress-bar">
              <div
                className="aventure-progress-fill"
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              />
            </div>
            <span className="aventure-progress-text">
              Scénario {progress.current}/{progress.total}
            </span>
          </div>
        )}
      </header>

      {error && (
        <div className="aventure-error">
          <p>{error}</p>
          <button onClick={() => setError('')}>Fermer</button>
        </div>
      )}

      <main className="aventure-content">

        {/* ── LISTE DES AVENTURES ── */}
        {phase === 'adventures' && (
          <div className="aventure-list">
            {module1Required && (
              <div className="aventure-module1-warning">
                <span className="warning-icon">🔐</span>
                <div className="warning-body">
                  <p>
                    Tu dois d'abord terminer{' '}
                    <strong>Le Miroir des Émotions</strong> pour débloquer ce jeu !
                  </p>
                  <button
                    className="warning-btn"
                    onClick={() => navigate('/app/jeux/miroir-emotions')}
                  >
                    Jouer au Miroir des Émotions
                  </button>
                </div>
              </div>
            )}

            {loading && !module1Required && (
              <p className="aventure-loading">Chargement des aventures...</p>
            )}

            {!loading && !module1Required && adventures.length === 0 && (
              <p className="aventure-empty">Aucune aventure disponible pour le moment.</p>
            )}

            <div className="aventure-cards-grid">
              {adventures.map((adventure) => (
                <div
                  key={adventure.id}
                  className={`aventure-card${!adventure.is_unlocked ? ' locked' : ''}${adventure.is_completed ? ' completed' : ''}`}
                  onClick={() => handleSelectAdventure(adventure)}
                  role="button"
                  aria-disabled={!adventure.is_unlocked}
                  tabIndex={adventure.is_unlocked ? 0 : -1}
                  onKeyDown={(e) => e.key === 'Enter' && handleSelectAdventure(adventure)}
                >
                  <div className="aventure-card-icon">
                    {adventure.is_completed
                      ? '🏆'
                      : adventure.is_unlocked
                      ? '🌟'
                      : '🔒'}
                  </div>
                  <div className="aventure-card-body">
                    <h3>{adventure.title}</h3>
                    <p>{adventure.description}</p>
                    <div className="aventure-card-meta">
                      <span className="meta-item">
                        📖 {adventure.scenarios_count} scénario{adventure.scenarios_count > 1 ? 's' : ''}
                      </span>
                      {adventure.best_score !== null && (
                        <span className="meta-item meta-score">
                          ⭐ Meilleur&nbsp;: {adventure.best_score}%
                        </span>
                      )}
                    </div>
                    <div className="aventure-card-cta">
                      {adventure.is_unlocked ? (
                        <span className="card-cta-btn">
                          {adventure.is_completed ? 'Rejouer' : 'Jouer'}
                        </span>
                      ) : (
                        <span className="card-cta-btn locked-cta">Verrouillé</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── PHASE DE JEU ── */}
        {phase === 'playing' && scenario && (
          <div className="aventure-game">
            {/* Narratif */}
            <div className="aventure-narrative">
              <div className="narrative-character">
                <div className="character-avatar">
                  {scenario.character_name?.[0]?.toUpperCase() || '?'}
                </div>
                <span className="character-name">{scenario.character_name}</span>
              </div>
              <div className="narrative-bubble">
                <p className="narrative-text">{scenario.narrative}</p>
              </div>
            </div>

            {/* Phase Émotion */}
            {gamePhase === 'emotion' && (
              <div className="aventure-phase emotion-phase">
                <h2 className="phase-question">{question}</h2>
                <div className="emotion-choices-grid">
                  {emotionChoices.map((emotion) => (
                    <button
                      key={emotion.id}
                      className="emotion-choice-btn"
                      disabled={submitting}
                      onClick={() => handleIdentifyEmotion(emotion.id)}
                    >
                      <span className="emotion-btn-emoji">{getEmotionEmoji(emotion.name)}</span>
                      <span className="emotion-btn-name">{emotion.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Phase Action */}
            {gamePhase === 'action' && (
              <div className="aventure-phase action-phase">
                <h2 className="phase-question">{question}</h2>
                <div className="action-choices-list">
                  {actionChoices.map((choice) => (
                    <button
                      key={choice.id}
                      className="action-choice-btn"
                      disabled={submitting}
                      onClick={() => handleChooseAction(choice.id)}
                    >
                      {choice.content}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── RÉSULTATS ── */}
        {phase === 'results' && results && (
          <div className="aventure-results">
            <div className="results-trophy">
              {results.passed ? '🏆' : '💪'}
            </div>
            <h2 className={results.passed ? 'results-title-win' : 'results-title-try'}>
              {results.passed ? 'Félicitations !' : 'Bel effort !'}
            </h2>
            <p className="results-subtitle">
              {results.passed
                ? "Tu as brillamment aidé tes amis !"
                : "Continue de t'entraîner pour progresser !"}
            </p>

            {results.is_new_best_score && (
              <div className="results-new-best">Nouveau meilleur score !</div>
            )}

            <div className="results-score-circle">
              <span className="score-value">{results.score}</span>
              <span className="score-max">/ {results.max_score}</span>
            </div>

            <div className="results-xp">
              <span>+{results.xp_earned} XP gagnés !</span>
            </div>

            <div className="results-details">
              <div className="result-detail">
                <span className="detail-label">Scénarios réussis</span>
                <span className="detail-value">
                  {results.scenarios_succeeded}/{results.scenarios_completed}
                </span>
              </div>
              <div className="result-detail">
                <span className="detail-label">Taux de réussite</span>
                <span className="detail-value">
                  {typeof results.success_percentage === 'number'
                    ? `${results.success_percentage.toFixed(0)}%`
                    : '-'}
                </span>
              </div>
            </div>

            {results.next_adventure_unlocked && results.next_adventure && (
              <div className="results-next-adventure">
                <span>🔓</span>
                <p>
                  Nouvelle aventure débloquée&nbsp;:{' '}
                  <strong>{results.next_adventure.title}</strong>
                </p>
              </div>
            )}

            <div className="results-actions">
              <button className="results-btn btn-adventures" onClick={handleBackToAdventures}>
                Choisir une aventure
              </button>
              <button
                className="results-btn btn-back"
                onClick={() => navigate('/app/jeux')}
              >
                Retour aux jeux
              </button>
            </div>
          </div>
        )}
      </main>

      {/* ── MODAL FEEDBACK ── */}
      {showFeedback && (
        <div
          className="feedback-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Retour du jeu"
        >
          <div className={`feedback-modal${feedbackData.correct ? ' feedback-success' : ' feedback-error'}`}>
            <div className="feedback-big-icon">
              {feedbackData.correct ? '✨' : '💭'}
            </div>
            <p className="feedback-message">{feedbackData.message}</p>

            {feedbackData.consequence && (
              <p className="feedback-consequence">{feedbackData.consequence}</p>
            )}

            {feedbackData.hint && (
              <p className="feedback-hint">
                <span className="hint-icon">💡</span> {feedbackData.hint}
              </p>
            )}

            {feedbackData.correct_answer && (
              <div className="feedback-correct-answer">
                <p className="correct-answer-label">La bonne réponse était :</p>
                <p className="correct-answer-text">"{feedbackData.correct_answer}"</p>
              </div>
            )}

            <button className="feedback-continue-btn" onClick={handleContinue}>
              Continuer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AventureAmitie;
